// src/app.tsx
import React from 'react';
import { SelectLang } from '@/components';
import { currentUser as queryCurrentUser } from '@/services/ant-design-pro/api';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import type { RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { Button, Space, Typography, Avatar, message } from 'antd';
import { LoginOutlined, UserAddOutlined, LogoutOutlined, YoutubeOutlined, GlobalOutlined } from '@ant-design/icons';
import { DefaultFooter } from '@ant-design/pro-components';
import defaultSettings from '../config/defaultSettings';
import {API_URL, API_URL_NO_PORT} from '../config/globalConfig';
import axiosInstance from "@/axiosInstance";

const { Text } = Typography;
const loginPath = '/user/login';
const registerPath = '/user/register';

export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      const msg = await queryCurrentUser({ skipErrorHandler: true });
      console.info("msg:", msg);
      if (msg.data) {
        return msg.data;
      }
      throw new Error('User data not found');
    } catch (error) {
      console.error('User info fetch error:', error);
    }
    return undefined;
  };

  // 检查当前路径是否为公开页面
  const publicPaths = [loginPath, registerPath];
  const isPublicPath = publicPaths.includes(history.location.pathname);

  if (!isPublicPath) {
    // 对于受保护的页面，必须检查用户登录状态
    const currentUser = await fetchUserInfo();
    if (currentUser) {
      console.info("currentUser:", currentUser);
      return {
        fetchUserInfo,
        currentUser,
        settings: defaultSettings as Partial<LayoutSettings>,
      };
    } else {
      // 用户未登录，强制跳转到登录页
      console.warn('User not authenticated, redirecting to login page');
      history.push(loginPath);
      return {
        fetchUserInfo,
        settings: defaultSettings as Partial<LayoutSettings>,
      };
    }
  }

  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

const logout = async () => {
  // 调用后端的注销接口
  await axiosInstance.post('/user/logout');
  sessionStorage.removeItem('token');
  message.success('退出登录成功！');
  history.push(loginPath);
};

export const layout: RunTimeLayoutConfig = ({ initialState, location }) => {
  // 检查用户是否已登录
  const isLoggedIn = !!(initialState?.currentUser?.data);
  
  // 公开页面列表（不需要layout的页面）
  const publicPaths = [loginPath, registerPath];
  const isPublicPath = publicPaths.includes(location?.pathname || '');
  
  // 如果是公开页面，不显示layout
  if (isPublicPath) {
    return {
      layout: false,
    };
  }
  
  // 如果用户未登录，不显示菜单和侧边栏
  if (!isLoggedIn) {
    return {
      layout: false, // 完全隐藏layout
      rightContentRender: () => (
        <Space size="small" align="center">
          <SelectLang />
          <Button type="primary" icon={<LoginOutlined />} onClick={() => history.push(loginPath)} style={{ padding: '0 12px' }}>
            登录
          </Button>
          <Button type="default" icon={<UserAddOutlined />} onClick={() => history.push(registerPath)} style={{ padding: '0 12px' }}>
            注册
          </Button>
        </Space>
      ),
    };
  }

  return {
    rightContentRender: () => (
      <Space size="small" align="center">
        <SelectLang />
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Avatar src={initialState.currentUser.data.avatar} size="small" style={{ marginRight: 8 }} />
          <Text style={{ whiteSpace: 'nowrap', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {initialState.currentUser.data.name}
          </Text>
          <Button type="text" icon={<LogoutOutlined />} style={{ marginLeft: 8, padding: '0 8px' }} onClick={logout}>
            退出
          </Button>
        </div>
      </Space>
    ),
    footerRender: () => (
      <DefaultFooter
        copyright="币圈李白-科学投机出品"
        links={[
          {
            key: 'youtube',
            title: (<><YoutubeOutlined /> YouTube-币圈李白</>),
            href: 'https://www.youtube.com/@BTC-libai12345/videos',
            blankTarget: true,
          },
          {
            key: '网站',
            title: (<><GlobalOutlined /> 网站-科学投机</>),
            href: API_URL_NO_PORT+'/welcome',
            blankTarget: true,
          },
        ]}
      />
    ),
    menuHeaderRender: undefined,
    // 只有登录用户才显示菜单
    menuRender: isLoggedIn ? undefined : false,
    // @ts-ignore
    ...initialState?.settings,
  };
};
